// =============================================================================
// Origin UI — src/pages/admin/HelpFeedbackAdminTab.tsx
// Admin tab for reviewing help assistant feedback (thumbs-down responses).
// =============================================================================

import { useState, useEffect } from 'react';
import { helpAdminApi } from '@/services/helpAdminApi';
import type { FeedbackListItem } from '@/services/helpAdminApi';
import { Card, ErrorBanner, ConfirmDialog } from '@/components/common';
import styles from './admin.module.css';

export function HelpFeedbackAdminTab() {
  const [items, setItems] = useState<FeedbackListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<'all' | 'unreviewed' | 'reviewed'>('unreviewed');

  // Expanded item
  const [expanded, setExpanded] = useState<string | null>(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  async function loadFeedback() {
    try {
      setLoading(true);
      const filterParam = filter === 'all' ? undefined : filter;
      const data = await helpAdminApi.listFeedback(filterParam);
      setItems(data);
    } catch (e: any) {
      setError(e.message || 'Failed to load feedback');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadFeedback(); }, [filter]);

  async function markReviewed(id: string) {
    try {
      setSaving(true);
      setError('');
      await helpAdminApi.reviewFeedback(id, reviewNotes);
      setExpanded(null);
      setReviewNotes('');
      await loadFeedback();
    } catch (e: any) {
      setError(e.message || 'Failed to update');
    } finally {
      setSaving(false);
    }
  }

  async function confirmDelete() {
    if (!deleteId) return;
    try {
      setError('');
      await helpAdminApi.deleteFeedback(deleteId);
      setDeleteId(null);
      if (expanded === deleteId) setExpanded(null);
      await loadFeedback();
    } catch (e: any) {
      setError(e.message || 'Failed to delete');
    }
  }

  const unreviewedCount = items.filter(i => !i.isReviewed).length;

  if (loading) return <div style={{ padding: 24, color: '#6B7280' }}>Loading feedback...</div>;

  return (
    <div>
      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h4 className={styles.formTitle}>
            Feedback
            {unreviewedCount > 0 && (
              <span style={{
                marginLeft: 8, padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                background: '#FEF3C7', color: '#D97706',
              }}>
                {unreviewedCount} unreviewed
              </span>
            )}
          </h4>
          <select
            value={filter}
            onChange={e => setFilter(e.target.value as any)}
            style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, cursor: 'pointer' }}
          >
            <option value="unreviewed">Unreviewed</option>
            <option value="reviewed">Reviewed</option>
            <option value="all">All</option>
          </select>
        </div>

        {items.length === 0 ? (
          <div style={{ padding: '32px 0', textAlign: 'center', color: '#94a3b8' }}>
            {filter === 'unreviewed' ? 'No unreviewed feedback' : 'No feedback entries'}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {items.map(item => (
              <div
                key={item.feedbackId}
                style={{
                  border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden',
                  background: expanded === item.feedbackId ? '#f9fafb' : '#fff',
                }}
              >
                {/* Summary row */}
                <div
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                    cursor: 'pointer', fontSize: 13,
                  }}
                  onClick={() => {
                    setExpanded(expanded === item.feedbackId ? null : item.feedbackId);
                    setReviewNotes(item.reviewNotes || '');
                  }}
                >
                  <span style={{ color: '#6B7280', whiteSpace: 'nowrap', fontSize: 12 }}>
                    {new Date(item.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  </span>
                  <span style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>{item.userDisplayName}</span>
                  <span style={{ color: '#6B7280', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {item.userQuestion}
                  </span>
                  <span style={{
                    padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
                    background: item.isReviewed ? '#F0FDF4' : '#FEF3C7',
                    color: item.isReviewed ? '#22C55E' : '#D97706',
                  }}>
                    {item.isReviewed ? 'Reviewed' : 'Pending'}
                  </span>
                </div>

                {/* Expanded detail */}
                {expanded === item.feedbackId && (
                  <div style={{ padding: '0 14px 14px 14px', borderTop: '1px solid #e5e7eb' }}>
                    <div style={{ marginTop: 12 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', marginBottom: 4, textTransform: 'uppercase' }}>Question</div>
                      <div style={{ padding: '8px 12px', background: '#fff', borderRadius: 6, border: '1px solid #e5e7eb', fontSize: 13, lineHeight: 1.5 }}>
                        {item.userQuestion}
                      </div>
                    </div>

                    <div style={{ marginTop: 12 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', marginBottom: 4, textTransform: 'uppercase' }}>Assistant Response</div>
                      <div style={{ padding: '8px 12px', background: '#fff', borderRadius: 6, border: '1px solid #e5e7eb', fontSize: 13, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                        {item.assistantResponse}
                      </div>
                    </div>

                    <div style={{ marginTop: 12, fontSize: 12, color: '#6B7280' }}>
                      Role: {item.userRole}
                    </div>

                    {item.isReviewed && item.reviewNotes && (
                      <div style={{ marginTop: 12 }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: '#22C55E', marginBottom: 4, textTransform: 'uppercase' }}>Review Notes</div>
                        <div style={{ padding: '8px 12px', background: '#F0FDF4', borderRadius: 6, fontSize: 13 }}>
                          {item.reviewNotes}
                        </div>
                        <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>
                          Reviewed by {item.reviewedBy} on {new Date(item.reviewedAt!).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </div>
                      </div>
                    )}

                    {!item.isReviewed && (
                      <div style={{ marginTop: 12 }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', marginBottom: 4, textTransform: 'uppercase' }}>Review Notes</div>
                        <textarea
                          value={reviewNotes}
                          onChange={e => setReviewNotes(e.target.value)}
                          placeholder="What was done? (e.g. Updated KB-03 leave section)"
                          rows={3}
                          style={{
                            width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #d1d5db',
                            fontSize: 13, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box',
                          }}
                        />
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                      {!item.isReviewed && (
                        <button className={styles.saveBtn} onClick={() => markReviewed(item.feedbackId)} disabled={saving} style={{ fontSize: 13 }}>
                          {saving ? 'Saving...' : 'Mark as Reviewed'}
                        </button>
                      )}
                      <button
                        style={{ background: 'none', border: '1px solid #DC2626', color: '#DC2626', padding: '6px 14px', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}
                        onClick={() => setDeleteId(item.feedbackId)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      <ConfirmDialog
        open={!!deleteId}
        title="Delete Feedback"
        message="Delete this feedback entry? This cannot be undone."
        onConfirm={confirmDelete}
        onCancel={() => setDeleteId(null)}
      />
    </div>
  );
}
